import {
  Box, Button, CircularProgress, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createService, getService, updateService } from '../../api/services';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';

interface ServiceFormState {
  name: string;
  description: string;
  priceDollars: string;
  isActive: boolean;
}

const empty: ServiceFormState = {
  name: '',
  description: '',
  priceDollars: '',
  isActive: true,
};

export function ServiceEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { request: getRequest } = useApiCallWithAuth(getService);
  const { request: createRequest } = useApiCallWithAuth(createService);
  const { request: updateRequest } = useApiCallWithAuth(updateService);

  const [form, setForm] = useState<ServiceFormState>(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    getRequest(id)
      .then(data => {
        setForm({
          name: data.name,
          description: data.description ?? '',
          priceDollars: (data.priceCents / 100).toFixed(2),
          isActive: data.isActive,
        });
      })
      .catch(() => setError('Failed to load service'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  function set<K extends keyof ServiceFormState>(field: K, value: ServiceFormState[K]) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    const priceCents = Math.round(parseFloat(form.priceDollars) * 100);

    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!Number.isFinite(priceCents) || priceCents <= 0) { setError('Price must be a positive amount.'); return; }

    setSaving(true);
    try {
      if (isEdit && id) {
        await updateRequest(id, {
          name: form.name,
          description: form.description || undefined,
          priceCents,
          isActive: form.isActive,
        });
      } else {
        await createRequest({
          name: form.name,
          description: form.description || undefined,
          priceCents,
        });
      }
      navigate('/services/admin');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 3 }}>
        <Typography variant='h5' fontWeight={700}>{isEdit ? 'Edit Service' : 'New Service'}</Typography>
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' onClick={() => navigate('/services/admin')}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </Stack>
      </Stack>

      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      <Stack spacing={2.5}>
        <TextField
          label='Name'
          value={form.name}
          onChange={e => set('name', e.target.value)}
          fullWidth
          required
        />

        <TextField
          label='Description'
          value={form.description}
          onChange={e => set('description', e.target.value)}
          fullWidth
          multiline
          rows={4}
        />

        <TextField
          label='Price (USD)'
          type='number'
          value={form.priceDollars}
          onChange={e => set('priceDollars', e.target.value)}
          fullWidth
          required
          inputProps={{ step: '0.01', min: '0' }}
        />

        {isEdit && (
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e => set('isActive', e.target.checked)}
              />
            }
            label={form.isActive ? 'Active' : 'Inactive'}
          />
        )}
      </Stack>
    </Box>
  );
}
